import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useProjects, useProjectsOptions, useProjectsDashboard, useClientsOptions, useManagersOptions, useCategoriesOptions } from '../../hooks/useProjects'
import { useAuth } from '../../contexts/AuthContext'
import api from '../../utils/api'
import { 
  Card, Table, Button, Space, Typography, Input, Modal, Form, Select, message,
  Popconfirm, Row, Col, Statistic, Tag, Badge, Tooltip, DatePicker, Drawer, Progress, Switch
} from 'antd'
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, EyeOutlined,
  ProjectOutlined, FilterOutlined, ReloadOutlined, WarningOutlined,
  CalendarOutlined, UserOutlined, TeamOutlined, ClockCircleOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined, PauseCircleOutlined,
  OrderedListOutlined,
  RocketOutlined, StopOutlined, BarChartOutlined, TeamOutlined as TasksOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import 'dayjs/locale/th'
import 'dayjs/plugin/customParseFormat'
import 'dayjs/plugin/isSameOrAfter'
import 'dayjs/plugin/isSameOrBefore'

// เพิ่ม plugins ให้ dayjs
dayjs.extend(require('dayjs/plugin/customParseFormat'))
dayjs.extend(require('dayjs/plugin/isSameOrAfter'))
dayjs.extend(require('dayjs/plugin/isSameOrBefore'))

const { Title, Text } = Typography
const { Search } = Input
const { Option } = Select

dayjs.locale('th')

const ProjectsListPage = () => {
  const navigate = useNavigate()
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [detailModalVisible, setDetailModalVisible] = useState(false)
  const [editingProject, setEditingProject] = useState(null)
  const [viewingProject, setViewingProject] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedPriority, setSelectedPriority] = useState('')
  const [selectedManager, setSelectedManager] = useState('')
  const [selectedClient, setSelectedClient] = useState('')
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 })
  const [form] = Form.useForm()

  // Use hooks
  const { user } = useAuth()
  const { 
    projects: projectsData, 
    pagination: projectsPagination, 
    loading: projectsLoading, 
    refreshProjects,
    deleteProject 
  } = useProjects({
    page: pagination.current,
    limit: pagination.pageSize,
    search: searchTerm,
    status: selectedStatus,
    category: selectedCategory,
    priority: selectedPriority,
    manager_id: selectedManager,
    client_id: selectedClient,
    autoFetch: true,
    enableCache: false
  })

  const { managerOptions } = useManagersOptions()
  const { clientOptions } = useClientsOptions()
  const { categoryOptions } = useCategoriesOptions()

  const { dashboardData: dashboardStats } = useProjectsDashboard()

  // Project status types
  const statusTypes = [
    { value: 'planning', label: 'วางแผน', icon: <ClockCircleOutlined />, color: 'blue' },
    { value: 'active', label: 'กำลังดำเนินการ', icon: <RocketOutlined />, color: 'green' },
    { value: 'on_hold', label: 'พักชั่วคราว', icon: <PauseCircleOutlined />, color: 'orange' },
    { value: 'completed', label: 'เสร็จสิ้น', icon: <CheckCircleOutlined />, color: 'purple' },
    { value: 'cancelled', label: 'ยกเลิก', icon: <StopOutlined />, color: 'red' }
  ]

  // Project priority types
  const priorityTypes = [
    { value: 'low', label: 'ต่ำ', color: 'default' },
    { value: 'medium', label: 'ปานกลาง', color: 'blue' },
    { value: 'high', label: 'สูง', color: 'orange' },
    { value: 'urgent', label: 'เร่งด่วน', color: 'red' }
  ]

  // Project categories - ใช้ hook แทน hardcode

  useEffect(() => {
    setProjects(projectsData || [])
    setPagination(prev => ({
      ...prev,
      total: projectsPagination?.total || 0
    }))
  }, [projectsData, projectsPagination])

  // Permission checks
  const canCreate = user?.permissions?.projects?.create === true
  const canUpdate = user?.permissions?.projects?.update === true
  const canDelete = user?.permissions?.projects?.delete === true
  const canView = user?.permissions?.projects?.read === true

  const handleAddProject = () => {
    if (!canCreate) {
      message.error('คุณไม่มีสิทธิ์ในการสร้างโครงการใหม่')
      return
    }
    setEditingProject(null)
    setModalVisible(true)
    form.resetFields()
  }

  const handleEditProject = (project) => {
    if (!canUpdate) {
      message.error('คุณไม่มีสิทธิ์ในการแก้ไขโครงการ')
      return
    }
    setEditingProject(project)
    setModalVisible(true)
    
    // Format dates for form - แก้ไขการจัดการ date values ให้ปลอดภัย
    const formData = { 
      ...project,
      // จัดการ date fields ด้วยการตรวจสอบและ format อย่างปลอดภัย
      start_date: project.start_date ? dayjs(project.start_date) : null,
      end_date: project.end_date ? dayjs(project.end_date) : null,
      expected_completion_date: project.expected_completion_date ? dayjs(project.expected_completion_date) : null,
      // จัดการ number fields
      progress_percentage: project.progress_percentage || 0,
      estimated_budget: project.estimated_budget || 0,
      actual_cost: project.actual_cost || 0,
      // จัดการ array fields
      team_members: project.team_members ? (typeof project.team_members === 'string' ? JSON.parse(project.team_members) : project.team_members) : [],
      tags: project.tags ? (typeof project.tags === 'string' ? JSON.parse(project.tags) : project.tags) : [],
      // จัดการ boolean fields
      is_active: project.is_active !== undefined ? project.is_active : true
    }
    
    console.log('📋 Setting form data:', formData)
    form.setFieldsValue(formData)
  }

  const handleViewProject = async (project) => {
    setViewingProject(project)
    setDetailModalVisible(true)
  }

  const handleDeleteProject = async (id) => {
    if (!canDelete) {
      message.error('คุณไม่มีสิทธิ์ในการลบโครงการ')
      return
    }
    try {
      await deleteProject(id)
      message.success('ลบโครงการสำเร็จ')
      await refreshProjects()
    } catch (error) {
      message.error('ไม่สามารถลบโครงการได้')
    }
  }

  const handleModalOk = async (values) => {
    try {
      console.log('🔄 Starting handleModalOk with values:', values)
      setLoading(true)
      
      // Format dates and set required defaults for NOT NULL fields
      const formattedValues = { 
        ...values,
        // Set default values for NOT NULL fields
        progress_percentage: values.progress_percentage || 0,
        estimated_budget: values.estimated_budget || 0,
        actual_cost: values.actual_cost || null,
        status: values.status || 'planning',
        priority: values.priority || 'medium',
        currency: values.currency || 'THB',
        is_active: values.is_active !== undefined ? values.is_active : true
      }
      
      // Format dates for API - ตรวจสอบให้แน่ใจว่าเป็น dayjs object
      if (values.start_date && values.start_date.isValid && values.start_date.isValid()) {
        formattedValues.start_date = values.start_date.format('YYYY-MM-DD')
      }
      if (values.end_date && values.end_date.isValid && values.end_date.isValid()) {
        formattedValues.end_date = values.end_date.format('YYYY-MM-DD')
      }
      if (values.expected_completion_date && values.expected_completion_date.isValid && values.expected_completion_date.isValid()) {
        formattedValues.expected_completion_date = values.expected_completion_date.format('YYYY-MM-DD')
      }

      // Format arrays and objects
      if (formattedValues.team_members && typeof formattedValues.team_members !== 'string') {
        formattedValues.team_members = JSON.stringify(formattedValues.team_members)
      }
      if (formattedValues.tags && typeof formattedValues.tags !== 'string') {
        formattedValues.tags = JSON.stringify(formattedValues.tags)
      }

      console.log('📝 Formatted values:', formattedValues)

      let response
      if (editingProject) {
        // UPDATE PROJECT
        console.log('🔧 Updating project with ID:', editingProject.id)
        
        response = await api.put(`/api/projects/${editingProject.id}`, formattedValues)
        console.log('📡 Update response:', response.data)
        
        if (response.data.success) {
          message.success('อัปเดตโครงการสำเร็จ!')
        } else {
          throw new Error(response.data.message || 'Failed to update project')
        }
      } else {
        // CREATE PROJECT
        console.log('➕ Creating new project')
        
        response = await api.post('/api/projects', formattedValues)
        console.log('📡 Create response:', response.data)
        
        if (response.data.success) {
          message.success('เพิ่มโครงการสำเร็จ!')
        } else {
          throw new Error(response.data.message || 'Failed to create project')
        }
      }

      console.log('✅ Project operation completed successfully')
      
      // Reset form and close modal
      setModalVisible(false)
      form.resetFields()
      setEditingProject(null)
      
      // Refresh projects list
      await refreshProjects()
      
    } catch (error) {
      console.error('❌ Error in handleModalOk:', error)
      
      // Handle different types of errors
      if (error.response?.status === 400) {
        const validationErrors = error.response.data?.errors
        if (validationErrors && validationErrors.length > 0) {
          const firstError = validationErrors[0]
          message.error(`ข้อมูลไม่ถูกต้อง: ${firstError.msg || firstError.message}`)
        } else {
          message.error(error.response.data?.message || 'ข้อมูลไม่ถูกต้อง')
        }
      } else if (error.response?.status === 403) {
        message.error('คุณไม่มีสิทธิ์ในการดำเนินการนี้')
      } else if (error.response?.status === 404) {
        message.error('ไม่พบโครงการที่ต้องการแก้ไข')
      } else if (error.code === 'NETWORK_ERROR' || !error.response) {
        message.error('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาลองใหม่อีกครั้ง')
      } else {
        message.error(error.message || 'ไม่สามารถบันทึกข้อมูลได้ กรุณาลองใหม่อีกครั้ง')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleModalCancel = () => {
    setModalVisible(false)
    form.resetFields()
    setEditingProject(null)
  }

  const handleSearch = (value) => {
    setSearchTerm(value)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleStatusFilter = (status) => {
    setSelectedStatus(status)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleCategoryFilter = (category) => {
    setSelectedCategory(category)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handlePriorityFilter = (priority) => {
    setSelectedPriority(priority)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleManagerFilter = (manager) => {
    setSelectedManager(manager)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleClientFilter = (client) => {
    setSelectedClient(client)
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  const handleTableChange = (paginationInfo) => {
    setPagination(prev => ({
      ...prev,
      current: paginationInfo.current,
      pageSize: paginationInfo.pageSize
    }))
  }

  const handleClearAllFilters = () => {
    setSearchTerm('')
    setSelectedStatus('')
    setSelectedCategory('')
    setSelectedPriority('')
    setSelectedManager('')
    setSelectedClient('')
    setPagination(prev => ({ ...prev, current: 1 }))
  }

  // จัดการงานย่อย
  const handleManageTasks = (project) => {
    if (user?.permissions?.project_tasks?.read !== true) {
      message.error('คุณไม่มีสิทธิ์ในการเข้าถึงระบบจัดการงานย่อย')
      return
    }
    navigate(`/projects/${project.id}/tasks`)
  }

  // Helper functions
  const getStatusConfig = (status) => {
    return statusTypes.find(s => s.value === status) || 
           { label: status, color: 'default', icon: <ClockCircleOutlined /> }
  }

  const getPriorityConfig = (priority) => {
    return priorityTypes.find(p => p.value === priority) || 
           { label: priority, color: 'default' }
  }

  const getCategoryConfig = (category) => {
    return categoryOptions.find(c => c.value === category) || 
           { label: category, color: 'default' }
  }

  const isProjectNearDeadline = (project) => {
    if (!project.end_date || project.status === 'completed') return false
    const endDate = dayjs(project.end_date)
    const today = dayjs()
    const daysDiff = endDate.diff(today, 'day')
    return daysDiff <= 7 && daysDiff > 0
  }

  const isProjectOverdue = (project) => {
    if (!project.end_date || project.status === 'completed') return false
    const endDate = dayjs(project.end_date)
    const today = dayjs()
    return endDate.isBefore(today)
  }

  // Desktop table columns
  const columns = [
    {
      title: 'ชื่อโครงการ',
      dataIndex: 'project_name',
      key: 'project_name',
      ellipsis: true,
      render: (name, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{name}</div>
          {record.project_code && (
            <div style={{ fontSize: '12px', color: '#666' }}>
              {record.project_code}
            </div>
          )}
        </div>
      )
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status, record) => {
        const config = getStatusConfig(status)
        const isOverdue = isProjectOverdue(record)
        const isNearDeadline = isProjectNearDeadline(record)
        
        return (
          <Tooltip title={isOverdue ? 'เกินกำหนด' : isNearDeadline ? 'ใกล้กำหนด' : config.label}>
            <Tag 
              color={isOverdue ? 'red' : config.color} 
              icon={
                isOverdue ? <ExclamationCircleOutlined /> :
                isNearDeadline ? <WarningOutlined /> : 
                config.icon
              }
            >
              {config.label}
            </Tag>
          </Tooltip>
        )
      }
    },
    {
      title: 'หมวดหมู่',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category) => {
        const config = getCategoryConfig(category)
        return <Tag color={config.color}>{config.label}</Tag>
      }
    },
    {
      title: 'ความสำคัญ',
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority) => {
        const config = getPriorityConfig(priority)
        return <Tag color={config.color}>{config.label}</Tag>
      }
    },
    {
      title: 'ความคืบหน้า',
      dataIndex: 'progress_percentage',
      key: 'progress_percentage',
      width: 120,
      render: (progress, record) => {
        const isOverdue = isProjectOverdue(record)
        const progressValue = progress || 0
        
        return (
          <div>
            <Progress 
              percent={progressValue} 
              size="small"
              strokeColor={isOverdue ? '#ff4d4f' : '#1890ff'}
              format={(percent) => `${percent}%`}
            />
            {isOverdue && (
              <div style={{ fontSize: '10px', color: '#ff4d4f', marginTop: 2 }}>
                เกินกำหนด
              </div>
            )}
          </div>
        )
      }
    },
    {
      title: 'วันที่เริ่ม',
      dataIndex: 'start_date',
      key: 'start_date',
      width: 100,
      render: (date) => date ? dayjs(date).format('DD/MM/YYYY') : '-'
    },
    {
      title: 'วันที่สิ้นสุด',
      dataIndex: 'end_date',
      key: 'end_date',
      width: 100,
      render: (date, record) => {
        if (!date) return '-'
        
        const isOverdue = isProjectOverdue(record)
        const isNearDeadline = isProjectNearDeadline(record)
        
        return (
          <span style={{ 
            color: isOverdue ? '#ff4d4f' : isNearDeadline ? '#fa8c16' : 'inherit',
            fontWeight: isOverdue || isNearDeadline ? 'bold' : 'normal'
          }}>
            {dayjs(date).format('DD/MM/YYYY')}
          </span>
        )
      }
    },
    {
      title: 'ลูกค้า',
      dataIndex: 'client_id',
      key: 'client_id',
      width: 120,
      ellipsis: true,
      render: (clientId, record) => {
        const clientName = record.client_name || record.client?.company_name || record.client?.name
        return clientName || '-'
      }
    },
    {
      title: 'ผู้จัดการ',
      dataIndex: 'manager_id',
      key: 'manager_id',
      width: 120,
      ellipsis: true,
      render: (managerId, record) => {
        const managerName = record.manager_name || record.manager?.full_name || record.manager?.username
        return managerName || '-'
      }
    },
    {
      title: 'การจัดการ',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          {canView && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewProject(record)}
              size="small"
              title="ดูรายละเอียด"
            />
          )}
          {user?.permissions?.project_tasks?.read === true && (
            <Button
              type="link"
              icon={<OrderedListOutlined />}
              onClick={() => handleManageTasks(record)}
              size="small"
              title="จัดการงานย่อย"
              style={{ color: '#1890ff' }}
            />
          )}
          {canUpdate && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditProject(record)}
              size="small"
              title="แก้ไข"
            />
          )}
          {canDelete && (
            <Popconfirm
              title="คุณแน่ใจว่าต้องการลบโครงการนี้?"
              onConfirm={() => handleDeleteProject(record.id)}
              okText="ใช่"
              cancelText="ไม่"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
                title="ลบ"
              />
            </Popconfirm>
          )}
        </Space>
      )
    }
  ]

  // Calculate statistics
  const totalProjects = pagination.total || 0
  const activeProjects = dashboardStats?.summary?.active || 0
  const completedProjects = dashboardStats?.summary?.completed || 0
  const overdueProjects = dashboardStats?.summary?.overdue || 0

  // Check if any filter is active
  const hasActiveFilters = searchTerm || selectedStatus || selectedCategory || selectedPriority || selectedManager || selectedClient

  // Mobile project card renderer
  const renderMobileProjectCard = (project) => {
    const statusConfig = getStatusConfig(project.status)
    const priorityConfig = getPriorityConfig(project.priority)
    const categoryConfig = getCategoryConfig(project.category)
    const isOverdue = isProjectOverdue(project)
    const isNearDeadline = isProjectNearDeadline(project)
    
    return (
      <Card
        key={project.id}
        size="small"
        style={{ marginBottom: 8 }}
        actions={[
          canView && (
            <Button
              type="link"
              icon={<EyeOutlined />}
              onClick={() => handleViewProject(project)}
              size="small"
            >
              ดู
            </Button>
          ),
          user?.permissions?.project_tasks?.read === true && (
            <Button
              type="link"
              icon={<TasksOutlined />}
              onClick={() => handleManageTasks(project)}
              size="small"
              style={{ color: '#1890ff' }}
            >
              จัดการงาน
            </Button>
          ),
          canUpdate && (
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEditProject(project)}
              size="small"
            >
              แก้ไข
            </Button>
          ),
          canDelete && (
            <Popconfirm
              title="ลบโครงการนี้?"
              onConfirm={() => handleDeleteProject(project.id)}
              okText="ใช่"
              cancelText="ไม่"
            >
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                size="small"
              >
                ลบ
              </Button>
            </Popconfirm>
          )
        ].filter(Boolean)}
      >
        <div style={{ marginBottom: 8 }}>
          <Text strong style={{ fontSize: 16 }}>{project.project_name}</Text>
          {project.project_code && (
            <div style={{ fontSize: 12, color: '#666' }}>
              {project.project_code}
            </div>
          )}
        </div>

        <div style={{ marginBottom: 8 }}>
          <Space wrap size={[4, 4]}>
            <Tag color={statusConfig.color} icon={statusConfig.icon} size="small">
              {statusConfig.label}
            </Tag>
            <Tag color={priorityConfig.color} size="small">
              {priorityConfig.label}
            </Tag>
            <Tag color={categoryConfig.color} size="small">
              {categoryConfig.label}
            </Tag>
          </Space>
        </div>

        <div style={{ marginBottom: 8 }}>
          <Progress 
            percent={project.progress_percentage || 0} 
            size="small"
            strokeColor={isOverdue ? '#ff4d4f' : '#1890ff'}
            format={(percent) => `ความคืบหน้า: ${percent}%`}
          />
        </div>

        <div style={{ fontSize: 12 }}>
          {project.start_date && (
            <div style={{ marginBottom: 2 }}>
              <CalendarOutlined style={{ marginRight: 4, color: '#1890ff' }} />
              เริ่ม: {dayjs(project.start_date).format('DD/MM/YYYY')}
            </div>
          )}
          {project.end_date && (
            <div style={{ 
              marginBottom: 2,
              color: isOverdue ? '#ff4d4f' : isNearDeadline ? '#fa8c16' : '#666'
            }}>
              <CalendarOutlined style={{ marginRight: 4 }} />
              สิ้นสุด: {dayjs(project.end_date).format('DD/MM/YYYY')}
              {isOverdue && ' (เกินกำหนด)'}
              {isNearDeadline && ' (ใกล้กำหนด)'}
            </div>
          )}
          {project.manager_name || project.manager?.full_name || project.manager?.username ? (
            <div style={{ marginBottom: 2 }}>
              <UserOutlined style={{ marginRight: 4, color: '#52c41a' }} />
              ผู้จัดการ: {project.manager_name || project.manager?.full_name || project.manager?.username}
            </div>
          ) : null}
          {project.client_name || project.client?.company_name || project.client?.name ? (
            <div style={{ marginBottom: 2 }}>
              <TeamOutlined style={{ marginRight: 4, color: '#722ed1' }} />
              ลูกค้า: {project.client_name || project.client?.company_name || project.client?.name}
            </div>
          ) : null}
        </div>
      </Card>
    )
  }

  return (
    <div style={{ padding: 24 }}>
      <Title level={2} style={{ fontSize: 24, marginBottom: 16 }}>
        <ProjectOutlined /> จัดการโครงการ
      </Title>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card size="small">
            <Statistic
              title="ทั้งหมด"
              value={totalProjects}
              prefix={<ProjectOutlined />}
              valueStyle={{ fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            size="small"
            onClick={() => handleStatusFilter('active')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="กำลังดำเนินการ"
              value={activeProjects}
              prefix={<RocketOutlined />}
              valueStyle={{ color: '#52c41a', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            size="small"
            onClick={() => handleStatusFilter('completed')}
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="เสร็จสิ้น"
              value={completedProjects}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#722ed1', fontSize: 20 }}
            />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card 
            size="small"
            style={{ cursor: 'pointer' }}
          >
            <Statistic
              title="เกินกำหนด"
              value={overdueProjects}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#ff4d4f', fontSize: 20 }}
            />
          </Card>
        </Col>
      </Row>

      {/* Search and Filters */}
      <Card style={{ marginBottom: 16 }} size="small">
        <Row gutter={16} align="middle">
          <Col xs={24} sm={6}>
            <Search
              placeholder="ค้นหาโครงการ..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={12} sm={3}>
            <Select
              value={selectedStatus}
              onChange={handleStatusFilter}
              style={{ width: '100%' }}
              placeholder="สถานะ"
              allowClear
            >
              {statusTypes.map(status => (
                <Option key={status.value} value={status.value}>
                  {status.icon} {status.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={3}>
            <Select
              value={selectedCategory}
              onChange={handleCategoryFilter}
              style={{ width: '100%' }}
              placeholder="หมวดหมู่"
              allowClear
            >
              {categoryOptions.map(category => (
                <Option key={category.value} value={category.value}>
                  {category.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={3}>
            <Select
              value={selectedPriority}
              onChange={handlePriorityFilter}
              style={{ width: '100%' }}
              placeholder="ความสำคัญ"
              allowClear
            >
              {priorityTypes.map(priority => (
                <Option key={priority.value} value={priority.value}>
                  {priority.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={3}>
            <Select
              value={selectedManager}
              onChange={handleManagerFilter}
              style={{ width: '100%' }}
              placeholder="ผู้จัดการ"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {managerOptions.map(manager => (
                <Option key={manager.value} value={manager.value}>
                  {manager.label}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={12} sm={3}>
            <Select
              value={selectedClient}
              onChange={handleClientFilter}
              style={{ width: '100%' }}
              placeholder="ลูกค้า"
              allowClear
              showSearch
              optionFilterProp="children"
            >
              {clientOptions.map(client => (
                <Option key={client.value} value={client.value}>
                  {client.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row style={{ marginTop: 16 }} justify="space-between" align="middle">
          <Col xs={24} sm={12}>
            <Button
              type="default"
              icon={<FilterOutlined />}
              onClick={handleClearAllFilters}
              size="small"
              disabled={!hasActiveFilters}
            >
              ล้างตัวกรองทั้งหมด
            </Button>
          </Col>
          <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
            {canCreate && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddProject}
              >
                เพิ่มโครงการใหม่
              </Button>
            )}
          </Col>
        </Row>
      </Card>

      {/* Projects Table */}
      <Card size="small">
        <Table
          columns={columns}
          dataSource={projects || []}
          rowKey="id"
          loading={loading || projectsLoading}
          scroll={{ x: 1400 }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`
          }}
          onChange={handleTableChange}
          locale={{ emptyText: 'ไม่มีข้อมูลโครงการ' }}
        />
      </Card>

      {/* Add/Edit Project Modal */}
      <Modal
        title={editingProject ? 'แก้ไขโครงการ' : 'เพิ่มโครงการใหม่'}
        open={modalVisible}
        onOk={() => form.submit()}
        onCancel={handleModalCancel}
        confirmLoading={loading}
        width={800}
        okText="บันทึก"
        cancelText="ยกเลิก"
        okButtonProps={{
          disabled: loading
        }}
        cancelButtonProps={{
          disabled: loading
        }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleModalOk}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="project_name"
                label="ชื่อโครงการ"
                rules={[{ required: true, message: 'กรุณาใส่ชื่อโครงการ' }]}
              >
                <Input placeholder="ชื่อโครงการ" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="project_code"
                label="รหัสโครงการ"
              >
                <Input placeholder="รหัสโครงการ" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="status"
                label="สถานะ"
                rules={[{ required: true, message: 'กรุณาเลือกสถานะ' }]}
              >
                <Select placeholder="เลือกสถานะ">
                  {statusTypes.map(status => (
                    <Option key={status.value} value={status.value}>
                      {status.icon} {status.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="category_id"
                label="หมวดหมู่"
                rules={[{ required: true, message: 'กรุณาเลือกหมวดหมู่' }]}
              >
                <Select placeholder="เลือกหมวดหมู่">
                  {categoryOptions.map(category => (
                    <Option key={category.value} value={category.value}>
                      {category.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="priority"
                label="ความสำคัญ"
                rules={[{ required: true, message: 'กรุณาเลือกความสำคัญ' }]}
              >
                <Select placeholder="เลือกความสำคัญ">
                  {priorityTypes.map(priority => (
                    <Option key={priority.value} value={priority.value}>
                      {priority.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="start_date"
                label="วันที่เริ่ม"
                rules={[{ required: true, message: 'กรุณาเลือกวันที่เริ่ม' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  placeholder="เลือกวันที่เริ่ม"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="end_date"
                label="วันที่สิ้นสุด"
                rules={[{ required: true, message: 'กรุณาเลือกวันที่สิ้นสุด' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  placeholder="เลือกวันที่สิ้นสุด"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="manager_id"
                label="ผู้จัดการโครงการ"
                rules={[{ required: true, message: 'กรุณาเลือกผู้จัดการโครงการ' }]}
              >
                <Select 
                  placeholder="เลือกผู้จัดการโครงการ"
                  showSearch
                  optionFilterProp="children"
                >
                  {managerOptions.map(manager => (
                    <Option key={manager.value} value={manager.value}>
                      {manager.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="client_id"
                label="ลูกค้า"
              >
                <Select 
                  placeholder="เลือกลูกค้า"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {clientOptions.map(client => (
                    <Option key={client.value} value={client.value}>
                      {client.label}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="expected_completion_date"
                label="วันที่คาดว่าจะเสร็จ"
              >
                <DatePicker 
                  style={{ width: '100%' }}
                  placeholder="เลือกวันที่คาดว่าจะเสร็จ"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="progress_percentage"
                label="ความคืบหน้า (%)"
                initialValue={0}
              >
                <Input 
                  type="number" 
                  min="0" 
                  max="100" 
                  placeholder="0"
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="currency"
                label="สกุลเงิน"
                initialValue="THB"
              >
                <Select placeholder="เลือกสกุลเงิน">
                  <Option value="THB">บาท (THB)</Option>
                  <Option value="USD">ดอลลาร์ (USD)</Option>
                  <Option value="EUR">ยูโร (EUR)</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="estimated_budget"
                label="งบประมาณประมาณ"
                initialValue={0}
              >
                <Input 
                  type="number" 
                  min="0" 
                  step="0.01"
                  placeholder="0"
                  style={{ width: '100%' }}
                  prefix="฿"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="actual_cost"
                label="ต้นทุนจริง"
                initialValue={0}
              >
                <Input 
                  type="number" 
                  min="0" 
                  step="0.01"
                  placeholder="0"
                  style={{ width: '100%' }}
                  prefix="฿"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="is_active"
                label="สถานะการใช้งาน"
                valuePropName="checked"
                initialValue={true}
              >
                <Switch checkedChildren="เปิดใช้งาน" unCheckedChildren="ปิดใช้งาน" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="team_members"
                label="สมาชิกทีม"
              >
                <Select 
                  mode="tags" 
                  placeholder="เพิ่มสมาชิกทีม..."
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="tags"
                label="แท็ก"
              >
                <Select 
                  mode="tags" 
                  placeholder="เพิ่มแท็ก..."
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="รายละเอียด"
          >
            <Input.TextArea
              rows={3}
              placeholder="รายละเอียดโครงการ..."
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Project Detail Modal */}
      <Modal
        title={`รายละเอียดโครงการ - ${viewingProject?.project_name}`}
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        width={800}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            ปิด
          </Button>
        ]}
      >
        {viewingProject && (
          <div>
            <Row gutter={16}>
              <Col span={12}>
                <Text strong>ชื่อโครงการ:</Text><br />
                <Text>{viewingProject.project_name}</Text>
              </Col>
              <Col span={12}>
                <Text strong>รหัสโครงการ:</Text><br />
                <Text>{viewingProject.project_code || '-'}</Text>
              </Col>
            </Row>
            
            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Text strong>สถานะ:</Text><br />
                {(() => {
                  const config = getStatusConfig(viewingProject.status)
                  return (
                    <Tag color={config.color} icon={config.icon}>
                      {config.label}
                    </Tag>
                  )
                })()}
              </Col>
              <Col span={12}>
                <Text strong>ความสำคัญ:</Text><br />
                {(() => {
                  const config = getPriorityConfig(viewingProject.priority)
                  return <Tag color={config.color}>{config.label}</Tag>
                })()}
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Text strong>วันที่เริ่ม:</Text><br />
                <Text>{viewingProject.start_date ? dayjs(viewingProject.start_date).format('DD/MM/YYYY') : '-'}</Text>
              </Col>
              <Col span={12}>
                <Text strong>วันที่สิ้นสุด:</Text><br />
                <Text style={{ 
                  color: isProjectOverdue(viewingProject) ? '#ff4d4f' : 'inherit',
                  fontWeight: isProjectOverdue(viewingProject) ? 'bold' : 'normal'
                }}>
                  {viewingProject.end_date ? dayjs(viewingProject.end_date).format('DD/MM/YYYY') : '-'}
                </Text>
              </Col>
            </Row>

            <Row gutter={16} style={{ marginTop: 16 }}>
              <Col span={12}>
                <Text strong>ผู้จัดการ:</Text><br />
                <Text>{viewingProject.manager_name || viewingProject.manager?.full_name || viewingProject.manager?.username || '-'}</Text>
              </Col>
              <Col span={12}>
                <Text strong>ลูกค้า:</Text><br />
                <Text>{viewingProject.client_name || viewingProject.client?.company_name || viewingProject.client?.name || '-'}</Text>
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Text strong>ความคืบหน้า:</Text><br />
              <Progress 
                percent={viewingProject.progress_percentage || 0}
                strokeColor={isProjectOverdue(viewingProject) ? '#ff4d4f' : '#1890ff'}
                format={(percent) => `${percent}%`}
              />
            </div>

            {viewingProject.description && (
              <div style={{ marginTop: 16 }}>
                <Text strong>รายละเอียด:</Text><br />
                <Text>{viewingProject.description}</Text>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  )
}

export default ProjectsListPage